import { IonInput, IonSelect, IonSelectOption } from '@ionic/react';
import React from 'react';
import {
  parseIfElseConfig,
  type AutomationBranchAction,
  type AutomationBranchActionType,
} from '../../automation/ifElseConfig';
import {
  BRANCH_ACTION_LABELS,
  getActionSubtitle,
  getBranchStatsFromLog,
} from '../../automation/workflowSummary';
import type { WorkflowValidationIssue } from '../../automation/workflowGraph';
import type {
  AutomationExecutionEntry,
  AutomationGraphNode,
} from '../../types/AutomationWorkflow.types';
import {
  StepCardHeader,
  type StepDragHandlers,
  type StepRunStatus,
} from './WorkflowStepCardParts';

export const WorkflowIfElseCard: React.FC<{
  node: AutomationGraphNode;
  executionLog: AutomationExecutionEntry[];
  selected: boolean;
  issues: WorkflowValidationIssue[];
  status: StepRunStatus;
  drag: StepDragHandlers;
  onSelect: () => void;
  onRemove: () => void;
  onConfigChange: (config: Record<string, unknown>) => void;
}> = ({
  node,
  executionLog,
  selected,
  issues,
  status,
  drag,
  onSelect,
  onRemove,
  onConfigChange,
}) => {
  const config = parseIfElseConfig(node.config);
  const stats = getBranchStatsFromLog(node.id, executionLog);

  const updateBranch = (
    branchKey: 'trueBranch' | 'falseBranch',
    patch: Partial<AutomationBranchAction>,
  ) => {
    const current = config[branchKey];
    onConfigChange({
      [branchKey]: {
        ...current,
        ...patch,
        config: { ...current.config, ...(patch.config ?? {}) },
      },
    });
  };

  const className = [
    'automation-step-card',
    'automation-step-card-action',
    'automation-step-card-if',
    selected ? 'selected' : '',
    issues.length ? 'invalid' : '',
    status.running ? 'running' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <article className={className} draggable {...drag}>
      <StepCardHeader
        actionType="if_else"
        title="If / else"
        subtitle={getActionSubtitle(node)}
        status={status}
        onSelect={onSelect}
        onRemove={onRemove}
      />

      <div className="automation-if-branches">
        <BranchSection
          label="IF"
          variant="if"
          detail={`BPM is between ${config.minBpm} and ${config.maxBpm}`}
          branch={config.trueBranch}
          statLabel={
            stats.total > 0 ? `${stats.trueCount} of ${stats.total} files` : ''
          }
          onBranchChange={(patch) => updateBranch('trueBranch', patch)}
        />
        <BranchSection
          label="OTHERWISE"
          variant="otherwise"
          detail="anything else"
          branch={config.falseBranch}
          statLabel={
            stats.total > 0 ? `${stats.falseCount} of ${stats.total} files` : ''
          }
          onBranchChange={(patch) => updateBranch('falseBranch', patch)}
        />
      </div>

      {selected && (
        <div className="automation-step-inline-config">
          <div className="automation-if-range">
            <IonInput
              label="Minimum BPM"
              labelPlacement="stacked"
              type="number"
              value={String(config.minBpm)}
              onIonInput={(event) =>
                onConfigChange({ minBpm: Number(event.detail.value ?? 0) })
              }
            />
            <IonInput
              label="Maximum BPM"
              labelPlacement="stacked"
              type="number"
              value={String(config.maxBpm)}
              onIonInput={(event) =>
                onConfigChange({ maxBpm: Number(event.detail.value ?? 0) })
              }
            />
          </div>
        </div>
      )}

      {issues.length > 0 && (
        <p className="automation-field-error">{issues[0].message}</p>
      )}
    </article>
  );
};

const BranchSection: React.FC<{
  label: string;
  variant: 'if' | 'otherwise';
  detail: string;
  branch: AutomationBranchAction;
  statLabel: string;
  onBranchChange: (patch: Partial<AutomationBranchAction>) => void;
}> = ({ label, variant, detail, branch, statLabel, onBranchChange }) => (
  <div className={`automation-if-branch automation-if-branch-${variant}`}>
    <div className="automation-if-branch-header">
      <span className="automation-if-branch-label">{label}</span>
      <span className="automation-if-branch-detail">{detail}</span>
    </div>
    <div className="automation-if-branch-action-card">
      <img
        src="/assets/icons/serato-dj-pro.svg"
        alt=""
        className="automation-if-branch-logo"
      />
      <div className="automation-if-branch-action-copy">
        <IonSelect
          interface="popover"
          aria-label="Branch action"
          value={branch.type}
          onIonChange={(event) =>
            onBranchChange({
              type: event.detail.value as AutomationBranchActionType,
              config: {},
            })
          }
        >
          {Object.entries(BRANCH_ACTION_LABELS).map(([value, actionLabel]) => (
            <IonSelectOption key={value} value={value}>
              {actionLabel}
            </IonSelectOption>
          ))}
        </IonSelect>
        {branch.type === 'add_to_serato_crate' && (
          <IonInput
            aria-label="Crate name"
            placeholder="Crate name"
            value={String(branch.config.crateName ?? '')}
            onIonInput={(event) =>
              onBranchChange({
                config: { crateName: event.detail.value ?? '' },
              })
            }
          />
        )}
      </div>
      {statLabel && (
        <span className="automation-if-branch-stat">{statLabel}</span>
      )}
    </div>
  </div>
);
